// SVG backend: layout primitives (display space, points) as SVG markup. The app's preview scales it
// onto the PDF.js page; the font is the bundled Noto Sans, loaded as the "Q2Q Sans" web font.

import type { FontStyle, PageLayout, Prim } from "./layout.ts";

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const n = (v: number) => String(Math.round(v * 100) / 100);
const FONT: Record<FontStyle, string> = {
  regular: `font-family="Q2Q Sans" font-weight="400"`,
  bold: `font-family="Q2Q Sans" font-weight="700"`,
  italic: `font-family="Q2Q Sans" font-style="italic"`,
};

function prim(p: Prim): string {
  switch (p.t) {
    case "rect": {
      const attrs = [`x="${n(p.x)}" y="${n(p.y)}" width="${n(p.w)}" height="${n(p.h)}"`, `fill="${p.fill ?? "none"}"`];
      if (p.fillOpacity !== undefined) attrs.push(`fill-opacity="${p.fillOpacity}"`);
      if (p.stroke) attrs.push(`stroke="${p.stroke}" stroke-width="${p.strokeWidth ?? 1}"`);
      if (p.strokeOpacity !== undefined) attrs.push(`stroke-opacity="${p.strokeOpacity}"`);
      if (p.dash) attrs.push(`stroke-dasharray="${p.dash.join(" ")}"`);
      return `<rect ${attrs.join(" ")}/>`;
    }
    case "line":
      return `<line x1="${n(p.x1)}" y1="${n(p.y1)}" x2="${n(p.x2)}" y2="${n(p.y2)}" stroke="${p.stroke}" stroke-width="${p.width}"${p.opacity !== undefined ? ` stroke-opacity="${p.opacity}"` : ""}${p.dash ? ` stroke-dasharray="${p.dash.join(" ")}"` : ""}/>`;
    case "circle":
      return `<circle cx="${n(p.cx)}" cy="${n(p.cy)}" r="${n(p.r)}" fill="${p.fill ?? "none"}"${p.stroke ? ` stroke="${p.stroke}" stroke-width="${p.strokeWidth ?? 1}"` : ""}/>`;
    case "poly":
      return `<polygon points="${p.points.map(([x, y]) => `${n(x)},${n(y)}`).join(" ")}" fill="${p.fill}"${p.opacity !== undefined ? ` fill-opacity="${p.opacity}"` : ""}/>`;
    case "text": {
      const clip = p.clip;
      const text = `<text x="${n(p.x - (clip?.[0] ?? 0))}" y="${n(p.y - (clip?.[1] ?? 0))}" font-size="${p.size}" ${FONT[p.font]} fill="${p.fill}">${esc(p.text)}</text>`;
      return clip
        ? `<svg x="${n(clip[0])}" y="${n(clip[1])}" width="${n(clip[2] - clip[0])}" height="${n(clip[3] - clip[1])}" overflow="hidden">${text}</svg>`
        : text;
    }
  }
}

/** The page's marks and labels as an SVG group, in points (scale it to the rendered page). */
export function svgGroup(layout: Pick<PageLayout, "prims">): string {
  return `<g class="q2q-layout" style="font-kerning:none;font-variant-ligatures:none">${layout.prims.map(prim).join("")}</g>`;
}

/** A standalone SVG of the layout (marks and labels only, no scan), for tests and debugging. */
export function svgDocument(layout: PageLayout): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${n(layout.width)}pt" height="${n(layout.height)}pt" viewBox="0 0 ${n(layout.width)} ${n(layout.height)}">${svgGroup(layout)}</svg>`;
}
